import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Brain, 
  Clock, 
  Trophy, 
  CheckCircle, 
  XCircle, 
  RotateCcw,
  User,
  Medal,
  Star
} from 'lucide-react';
import { useQuiz } from '@/hooks/quiz/useQuiz';
import { useQuizLeaderboard } from '@/hooks/quiz/useQuizLeaderboard';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { nb, enUS } from 'date-fns/locale';

interface QuizSectionProps {
  interviewId: string;
}

type QuizState = 'not-generated' | 'loading' | 'ready' | 'taking' | 'completed' | 'results';

// Helper to shuffle options and track correct answer position
interface ShuffledQuestion {
  options: string[];
  correctAnswerIndex: number;
}

const shuffleOptions = (options: string[], correctAnswer: number): ShuffledQuestion => {
  // Create array of indices
  const indices = options.map((_, i) => i);
  
  // Fisher-Yates shuffle
  for (let i = indices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }
  
  // Map shuffled options and find new correct answer index
  const shuffledOptions = indices.map(i => options[i]);
  const newCorrectIndex = indices.indexOf(correctAnswer);
  
  return {
    options: shuffledOptions,
    correctAnswerIndex: newCorrectIndex
  };
};

const QuizSection: React.FC<QuizSectionProps> = ({ interviewId }) => {
  const { t, i18n } = useTranslation();
  const { quiz, isLoading, isGenerating, error, generateQuiz, submitQuizAttempt } = useQuiz(interviewId);
  const { leaderboard, refetch: refetchLeaderboard } = useQuizLeaderboard(interviewId);
  
  const [quizState, setQuizState] = useState<QuizState>('not-generated');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<Record<number, number>>({});
  const [startTime, setStartTime] = useState<number>(0);
  const [lastAttempt, setLastAttempt] = useState<any>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  // Store shuffled questions to maintain consistent order during quiz
  const [shuffledQuestions, setShuffledQuestions] = useState<ShuffledQuestion[]>([]);

  useEffect(() => {
    console.log('[QuizSection] State update:', { isLoading, quiz: quiz ? { id: quiz.id, questionsCount: quiz.questions?.length } : null, error });
    
    if (isLoading) {
      setQuizState('loading');
    } else if (quiz) {
      // Additional validation for quiz data
      if (!quiz.questions || !Array.isArray(quiz.questions) || quiz.questions.length === 0) {
        console.error('[QuizSection] Invalid quiz data - no questions:', quiz);
        setQuizState('not-generated');
      } else {
        console.log('[QuizSection] Quiz ready with', quiz.questions.length, 'questions');
        setQuizState('ready');
      }
    } else if (!quiz && !error) {
      setQuizState('not-generated');
    }
  }, [quiz, isLoading, error]);

  const startQuiz = () => {
    if (!quiz || !quiz.questions) return;
    
    // Shuffle options for each question when starting
    const shuffled = quiz.questions.map(q => 
      shuffleOptions(q.options, q.correctAnswer)
    );
    setShuffledQuestions(shuffled);
    
    setQuizState('taking');
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setStartTime(Date.now());
    setShowExplanation(false);
  };

  const selectAnswer = (answerIndex: number) => {
    setUserAnswers(prev => ({
      ...prev,
      [currentQuestionIndex]: answerIndex
    }));
  };

  const nextQuestion = () => {
    if (!quiz || !quiz.questions || quiz.questions.length === 0) {
      console.error('No quiz or questions available');
      setQuizState('ready');
      return;
    }
    
    if (currentQuestionIndex < quiz.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setShowExplanation(false);
    } else {
      finishQuiz();
    }
  };

  const finishQuiz = async () => {
    if (!quiz || !quiz.questions || quiz.questions.length === 0) {
      console.error('Cannot finish quiz: no questions available');
      setQuizState('ready');
      return;
    }
    
    const timeSpent = Math.floor((Date.now() - startTime) / 1000);
    
    // Build a map of shuffled correct answer indices to pass to submitQuizAttempt
    const shuffledCorrectIndices: Record<number, number> = {};
    shuffledQuestions.forEach((sq, index) => {
      shuffledCorrectIndices[index] = sq.correctAnswerIndex;
    });
    
    try {
      const attempt = await submitQuizAttempt(userAnswers, timeSpent, shuffledCorrectIndices);
      setLastAttempt(attempt);
      setQuizState('completed');
      refetchLeaderboard();
    } catch (error) {
      console.error('Error submitting quiz:', error);
    }
  };

  const resetQuiz = () => {
    setQuizState('ready');
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setLastAttempt(null);
    setShowExplanation(false);
  };

  const showResults = () => {
    setQuizState('results');
  };

  const backToQuiz = () => {
    setQuizState('ready');
  };

  if (quizState === 'loading') {
    return (
      <Card className="md:border md:shadow-sm border-0 shadow-none">
        <CardContent className="pt-4 md:pt-6 px-0 md:px-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/4"></div>
            <div className="space-y-3">
              <div className="h-12 bg-muted rounded"></div>
              <div className="h-12 bg-muted rounded"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (quizState === 'not-generated') {
    return (
      <Card className="md:border md:shadow-sm border-0 shadow-none">
        <CardHeader className="px-0 md:px-6">
          <CardTitle className="flex items-center space-x-2 text-base md:text-lg">
            <Brain className="h-4 w-4 md:h-5 md:w-5" />
            <span>Quiz</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 md:space-y-6 px-0 md:px-6">
          <div className="text-center py-6 md:py-8">
            <Brain className="h-12 w-12 md:h-16 md:w-16 mx-auto mb-3 md:mb-4 text-muted-foreground" />
            <h3 className="text-base md:text-lg font-semibold mb-2">Test din kunnskap!</h3>
            <p className="text-sm md:text-base text-muted-foreground mb-4 md:mb-6 px-4">
              La Kai lage 5 spørsmål basert på denne artikkelen
            </p>
            <Button 
              onClick={generateQuiz} 
              disabled={isGenerating}
              size="lg"
              className="w-full max-w-[200px]"
            >
              {isGenerating ? (
                <>
                  <RotateCcw className="mr-2 h-4 w-4 animate-spin" />
                  Genererer...
                </>
              ) : (
                <>
                  <Brain className="mr-2 h-4 w-4" />
                  Generer Quiz
                </>
              )}
            </Button>
          </div>
          {error && (
            <div className="text-center text-destructive">
              <p>Feil: {error}</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  if (quizState === 'taking' && quiz) {
    // Safety check: ensure quiz has questions and currentQuestionIndex is valid
    if (!quiz.questions || quiz.questions.length === 0) {
      console.error('Quiz has no questions:', quiz);
      setQuizState('ready');
      return null;
    }

    if (currentQuestionIndex >= quiz.questions.length) {
      console.error('Question index out of bounds:', currentQuestionIndex, 'of', quiz.questions.length);
      setCurrentQuestionIndex(0);
      return null;
    }

    const currentQuestion = quiz.questions[currentQuestionIndex];
    
    // Additional safety check for question structure
    if (!currentQuestion || !currentQuestion.question || !currentQuestion.options) {
      console.error('Invalid question structure at index', currentQuestionIndex, ':', currentQuestion);
      setQuizState('ready');
      return null;
    }

    const selectedAnswer = userAnswers[currentQuestionIndex];
    
    return (
      <Card className="md:border md:shadow-sm border-0 shadow-none">
        <CardHeader className="px-0 md:px-6">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="flex items-center space-x-1.5 md:space-x-2 text-sm md:text-base">
              <Brain className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" />
              <span className="truncate">Quiz - Spørsmål {currentQuestionIndex + 1} av {quiz.questions.length}</span>
            </CardTitle>
            <Badge variant="outline" className="flex-shrink-0 text-xs">
              <Clock className="mr-1 h-3 w-3" />
              {Math.floor((Date.now() - startTime) / 1000)}s
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 md:space-y-6 px-0 md:px-6 overflow-hidden">
          <Progress 
            value={((currentQuestionIndex + 1) / quiz.questions.length) * 100} 
            className="w-full" 
          />
          
          <div className="space-y-3 md:space-y-4 overflow-hidden">
            <h3 className="text-base md:text-lg font-semibold">{currentQuestion.question}</h3>
            
            {/* Use shuffled options if available, otherwise fall back to original */}
            {(() => {
              const shuffled = shuffledQuestions[currentQuestionIndex];
              const displayOptions = shuffled ? shuffled.options : currentQuestion.options;
              const correctIndex = shuffled ? shuffled.correctAnswerIndex : currentQuestion.correctAnswer;
              
              return (
                <>
                  <div className="space-y-2 md:space-y-3">
                    {displayOptions.map((option, index) => (
                      <Button
                        key={index}
                        variant={selectedAnswer === index ? "default" : "outline"}
                        className="w-full justify-start text-left p-3 md:p-4 h-auto text-sm md:text-base whitespace-normal overflow-hidden"
                        onClick={() => selectAnswer(index)}
                      >
                        <span className="break-words overflow-hidden w-full">{option}</span>
                      </Button>
                    ))}
                  </div>

                  {selectedAnswer !== undefined && showExplanation && (
                    <div className="mt-3 md:mt-4 p-3 md:p-4 bg-muted rounded-lg">
                      <div className="flex items-start space-x-2">
                        {selectedAnswer === correctIndex ? (
                          <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-green-600 mt-0.5 flex-shrink-0" />
                        ) : (
                          <XCircle className="h-4 w-4 md:h-5 md:w-5 text-red-600 mt-0.5 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium mb-1 md:mb-2 text-sm md:text-base">
                            {selectedAnswer === correctIndex ? 'Riktig!' : 'Feil svar'}
                          </p>
                          <p className="text-xs md:text-sm text-muted-foreground break-words">
                            {currentQuestion.explanation}
                          </p>
                          {selectedAnswer !== correctIndex && (
                            <p className="text-xs md:text-sm font-medium mt-1 md:mt-2 break-words">
                              Riktig svar: {displayOptions[correctIndex]}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
          
          <div className="flex justify-between gap-2 flex-wrap md:flex-nowrap">
            <Button 
              variant="outline" 
              onClick={resetQuiz}
              size="sm"
              className="md:size-default text-xs md:text-sm"
            >
              Avbryt
            </Button>
            
            {selectedAnswer !== undefined && (
              <Button 
                onClick={() => {
                  if (!showExplanation) {
                    setShowExplanation(true);
                  } else {
                    nextQuestion();
                  }
                }}
                size="sm"
                className="md:size-default text-xs md:text-sm"
              >
                {!showExplanation ? 'Svar' : (
                  currentQuestionIndex === quiz.questions.length - 1 ? 'Fullfør' : 'Neste'
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (quizState === 'completed' && lastAttempt) {
    const percentage = Math.round((lastAttempt.score / lastAttempt.total_questions) * 100);
    
    return (
      <Card className="md:border md:shadow-sm border-0 shadow-none">
        <CardHeader className="px-0 md:px-6">
          <CardTitle className="flex items-center space-x-2 text-base md:text-lg">
            <Trophy className="h-4 w-4 md:h-5 md:w-5" />
            <span>Quiz Fullført!</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 md:space-y-6 px-0 md:px-6">
          <div className="text-center space-y-3 md:space-y-4">
            <div className="text-4xl md:text-6xl font-bold text-primary">
              {percentage}%
            </div>
            <div className="space-y-1 md:space-y-2">
              <p className="text-lg md:text-xl font-semibold">
                {lastAttempt.score} av {lastAttempt.total_questions} riktige
              </p>
              {lastAttempt.time_taken_seconds && (
                <p className="text-sm md:text-base text-muted-foreground">
                  Tid brukt: {Math.floor(lastAttempt.time_taken_seconds / 60)}m {lastAttempt.time_taken_seconds % 60}s
                </p>
              )}
            </div>
            
            <div className="pt-3 md:pt-4">
              {percentage >= 90 && (
                <Badge variant="default" className="text-sm md:text-lg p-1.5 md:p-2">
                  <Star className="mr-1 md:mr-2 h-3 w-3 md:h-4 md:w-4" />
                  Fantastisk!
                </Badge>
              )}
              {percentage >= 70 && percentage < 90 && (
                <Badge variant="secondary" className="text-sm md:text-lg p-1.5 md:p-2">
                  <Trophy className="mr-1 md:mr-2 h-3 w-3 md:h-4 md:w-4" />
                  Bra jobbet!
                </Badge>
              )}
              {percentage >= 50 && percentage < 70 && (
                <Badge variant="outline" className="text-sm md:text-lg p-1.5 md:p-2">
                  Ikke verst!
                </Badge>
              )}
              {percentage < 50 && (
                <Badge variant="outline" className="text-sm md:text-lg p-1.5 md:p-2">
                  Prøv igjen!
                </Badge>
              )}
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row gap-3 md:gap-4">
            <Button 
              variant="outline" 
              onClick={resetQuiz}
              className="flex-1 text-xs md:text-sm"
              size="sm"
            >
              <RotateCcw className="mr-1.5 md:mr-2 h-3 w-3 md:h-4 md:w-4" />
              Ta Quiz Igjen
            </Button>
            <Button 
              onClick={showResults}
              className="flex-1 text-xs md:text-sm"
              size="sm"
            >
              <Trophy className="mr-1.5 md:mr-2 h-3 w-3 md:h-4 md:w-4" />
              Se Topp 10
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (quizState === 'results') {
    return (
      <Card className="md:border md:shadow-sm border-0 shadow-none">
        <CardHeader className="px-0 md:px-6">
          <CardTitle className="flex items-center space-x-2 text-base md:text-lg">
            <Trophy className="h-4 w-4 md:h-5 md:w-5" />
            <span>Topp 10 Rangliste</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 md:space-y-6 px-0 md:px-6">
          {leaderboard.length > 0 ? (
            <div className="space-y-2 md:space-y-3">
              {leaderboard.map((entry, index) => (
                <div key={entry.id} className="flex items-center gap-2 md:gap-4 p-2 md:p-3 bg-muted rounded-lg">
                  <div className="flex items-center justify-center w-6 h-6 md:w-8 md:h-8 rounded-full bg-background flex-shrink-0">
                    {index === 0 && <Medal className="h-3 w-3 md:h-4 md:w-4 text-yellow-500" />}
                    {index === 1 && <Medal className="h-3 w-3 md:h-4 md:w-4 text-gray-400" />}
                    {index === 2 && <Medal className="h-3 w-3 md:h-4 md:w-4 text-amber-600" />}
                    {index > 2 && <span className="text-xs md:text-sm font-semibold">{index + 1}</span>}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 md:gap-2">
                      <User className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground flex-shrink-0" />
                      <span className="font-medium text-xs md:text-sm truncate">
                        {entry.profiles?.display_name || entry.profiles?.username || 'Anonym'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="text-right space-y-0.5 md:space-y-1 flex-shrink-0">
                    <div className="font-semibold text-xs md:text-sm whitespace-nowrap">
                      {entry.score}/{entry.total_questions} ({entry.percentage}%)
                    </div>
                    <div className="text-[10px] md:text-xs text-muted-foreground whitespace-nowrap">
                      {format(new Date(entry.completed_at), 'dd.MM.yyyy HH:mm', { 
                        locale: i18n.language === 'no' ? nb : enUS 
                      })}
                    </div>
                    {entry.time_taken_seconds && (
                      <div className="text-[10px] md:text-xs text-muted-foreground whitespace-nowrap">
                        {Math.floor(entry.time_taken_seconds / 60)}m {entry.time_taken_seconds % 60}s
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 md:py-8 text-muted-foreground">
              <Trophy className="h-10 w-10 md:h-12 md:w-12 mx-auto mb-3 md:mb-4 opacity-50" />
              <p className="text-sm md:text-base">Ingen har tatt denne quizen ennå.</p>
              <p className="text-sm md:text-base">Vær den første!</p>
            </div>
          )}
          
          <Separator />
          
          <div className="flex flex-col md:flex-row gap-3 md:gap-4">
            <Button 
              variant="outline" 
              onClick={backToQuiz}
              className="flex-1 text-xs md:text-sm"
              size="sm"
            >
              Tilbake til Quiz
            </Button>
            <Button 
              onClick={startQuiz}
              className="flex-1 text-xs md:text-sm"
              size="sm"
            >
              <Brain className="mr-1.5 md:mr-2 h-3 w-3 md:h-4 md:w-4" />
              Ta Quiz
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Default ready state
  return (
    <Card className="md:border md:shadow-sm border-0 shadow-none">
      <CardHeader className="px-0 md:px-6">
        <CardTitle className="flex items-center space-x-2 text-base md:text-lg">
          <Brain className="h-4 w-4 md:h-5 md:w-5" />
          <span>Quiz</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 md:space-y-6 px-0 md:px-6">
        <div className="text-center space-y-3 md:space-y-4">
          <Brain className="h-12 w-12 md:h-16 md:w-16 mx-auto text-primary" />
          <div>
            <h3 className="text-base md:text-lg font-semibold mb-1 md:mb-2">
              Quiz klar!
            </h3>
            <p className="text-sm md:text-base text-muted-foreground px-4">
              5 spørsmål om denne artikkelen venter på deg
            </p>
          </div>
          
          <div className="flex flex-col md:flex-row gap-3 md:gap-4">
            <Button 
              onClick={startQuiz}
              size="lg"
              className="flex-1 text-sm md:text-base"
            >
              <Brain className="mr-1.5 md:mr-2 h-3 w-3 md:h-4 md:w-4" />
              Start Quiz
            </Button>
            <Button 
              variant="outline"
              onClick={showResults}
              size="lg"
              className="flex-1 text-sm md:text-base"
            >
              <Trophy className="mr-1.5 md:mr-2 h-3 w-3 md:h-4 md:w-4" />
              Se Topp 10
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default QuizSection;